import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';
import { AutoUnsubscribe } from '../../common-explain/decorators/auto-unsubscribe';
import { ExplainMainModuleEnum, ExplainModule, ExplainModuleEnum } from '../../core/user/user.model';
import { UserSessionStore } from './user-session.store';

@AutoUnsubscribe
@Injectable({
  providedIn: 'root',
})
export class MainModuleManagerService {
  private _activeExplainMainModule?: ExplainMainModuleEnum;
  private _alternativeExplainMainModule!: ExplainMainModuleEnum | null;
  explainMainModuleChanged = new ReplaySubject<ExplainMainModuleEnum>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private sessionStore: UserSessionStore
  ) {
    this.updateActiveExplainModule();
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.updateActiveExplainModule();
    });
  }

  /***
   * Map between userModule and explainMainModule.
   */
  private static readonly ROUTE_TO_MODULE: Record<string, ExplainMainModuleEnum> = {
    territory_intel: ExplainMainModuleEnum.TERRITORY_INTEL,
    tenders: ExplainMainModuleEnum.TENDERS,
    response: ExplainMainModuleEnum.TENDERS,
  };

  /***
   * Common routes to all userModules.
   */
  private static readonly IGNORED_ROUTES = new Set(['settings', 'admin-interface']);

  updateActiveExplainModule() {
    const routeModule: string | undefined = this.route.firstChild?.snapshot.data['module'];
    // If no route module is found, return.
    if (!routeModule) return;
    // If the route module is in the ignored routes, return.
    if (this.activeExplainMainModule && MainModuleManagerService.IGNORED_ROUTES.has(routeModule)) return;
    // If no active explain main module is set, set the landing main module based on the user modules.
    if (this.activeExplainMainModule === undefined) {
      const userModules = this.sessionStore.userSession()?.modules;
      this.initExplainMainModule(userModules);
    }
    const mainModule = MainModuleManagerService.ROUTE_TO_MODULE[routeModule];
    // If the main module is found in the map, set the active explain main module.
    if (mainModule) {
      this.activeExplainMainModule = mainModule;
    }
  }

  /**
   * Method in charge of setting the main module post login.
   * @param userModules - The route modules to set.
   * All possible combinations of userModules:
   * (ET -> territory_intel)
   * (EM -> tenders)
   * (ER -> tenders)
   * (ET, EM -> tenders)
   * (ET, ER -> territory_intel)
   * (EM, ER -> tenders)
   * (ET, EM, ER -> tenders)
   */
  initExplainMainModule(userModules?: ExplainModule[]) {
    if (!userModules) return;

    const hasTenders = userModules.some(module => module.name === ExplainModuleEnum.TENDERS);
    const hasResponse = userModules.some(module => module.name === ExplainModuleEnum.RESPONSE);
    const hasTerritoryIntel = userModules.some(module => module.name === ExplainModuleEnum.TERRITORY_INTEL);

    if (hasTenders || (hasResponse && !hasTerritoryIntel)) {
      this.activeExplainMainModule = ExplainMainModuleEnum.TENDERS;
    } else {
      this.activeExplainMainModule = ExplainMainModuleEnum.TERRITORY_INTEL;
    }
  }

  /***
   * Method in charge of updating the value of the alternativeExplainMainModule.
   */
  updateAlternativeSuggestedModule(currentModule: ExplainMainModuleEnum) {
    const userModules = this.sessionStore.userSession()?.modules || [];
    if (
      userModules.some(module => module.name === ExplainModuleEnum.TERRITORY_INTEL) &&
      (userModules.some(module => module.name === ExplainModuleEnum.TENDERS) ||
        userModules.some(module => module.name === ExplainModuleEnum.RESPONSE))
    ) {
      this.alternativeExplainMainModule =
        currentModule === ExplainMainModuleEnum.TERRITORY_INTEL ? ExplainMainModuleEnum.TENDERS : ExplainMainModuleEnum.TERRITORY_INTEL;
    } else {
      this.alternativeExplainMainModule = null;
    }
  }

  /***
   * Method in charge of deciding where to land the user after login.
   * @param modules - The modules the user has.
   * @returns The route to land the user.
   */
  getPostLoginRoute(modules: ExplainModule[]): string {
    if (modules.length === 1 && modules[0].name === ExplainModuleEnum.RESPONSE) return '/tenders/responses';
    if (modules.some(module => module.name === ExplainModuleEnum.TENDERS)) {
      return '/tenders/watch';
    }
    if (modules.some(module => module.name === ExplainModuleEnum.RESPONSE)) {
      return '/tenders/responses';
    }
    return '/';
  }

  get isResponseOnlyUser(): boolean {
    const modules = this.sessionStore.userSession()?.modules ?? [];
    return modules.length === 1 && modules[0].name === ExplainModuleEnum.RESPONSE;
  }

  get userModules(): ExplainModule[] {
    return this.sessionStore.userSession()?.modules ?? [];
  }

  //region getters and setters

  /***
   * Setter updating explainMainModule, alternativeExplainMainModule and emitting the change event
   * via explainMainModuleChanged.
   * @param value - The value to set.
   */
  set activeExplainMainModule(value: ExplainMainModuleEnum) {
    this._activeExplainMainModule = value;
    this.updateAlternativeSuggestedModule(value);
    this.explainMainModuleChanged.next(value);
  }

  get activeExplainMainModule(): ExplainMainModuleEnum | undefined {
    return this._activeExplainMainModule;
  }

  get alternativeExplainMainModule(): ExplainMainModuleEnum | null {
    return this._alternativeExplainMainModule;
  }

  set alternativeExplainMainModule(value: ExplainMainModuleEnum | null) {
    this._alternativeExplainMainModule = value;
  }
  //endregion
}
